import { lazy, Suspense } from 'react'

import { TermsAcceptSheet } from '~/modules/user'
import { ErrorBoundary } from '~/shared/ui'

import { DevPanelGate } from './DevPanel'
import { AppProviders } from './providers/AppProviders'
import { useSession } from './providers/SessionProvider'
import { AppRouter } from './router/router'

/**
 * Ловец приглашений — единственное, что тянуло слайс `link` в бандл входа.
 *
 * Импорт остаётся через публичное API слайса (правило №2 архитектуры), но динамический:
 * статическое ребро означало бы, что полотно уз, экран человека и их стили грузятся до
 * первого кадра у всех — включая тех, кто открыл приложение впервые и идёт в онбординг.
 * Suspense с пустым фолбэком: ловец ничего не рисует, пока не разберёт стартовый параметр.
 *
 * `TermsAcceptSheet` оставлен статическим намеренно: слайс `user` всё равно нужен
 * SessionProvider'у на старте, и ленивая загрузка шторки не убрала бы из графа ничего.
 */
const InviteCatcher = lazy(() =>
  import('~/modules/link').then((m) => ({ default: m.InviteCatcher })),
)

/**
 * Ловец приглашений живёт рядом с роутером, а не внутри экрана.
 *
 * Стартовый параметр `inv_<token>` приходит один раз на запуск и достаётся тому, кто оказался
 * смонтирован первым, — а маршрут в этот момент может быть любым: онбординг у новенькой,
 * главная у зарегистрированного, гейт подписки у неподписанного. Пока разбор жил на странице
 * онбординга, все, кроме новеньких, теряли приглашение молча.
 *
 * `isEnabled` включается только на пройденной регистрации: связь создаётся после онбординга,
 * а не посреди него (правка основателя). Токен ловится и запоминается независимо от этого.
 */
function AppShell() {
  const { status } = useSession()

  return (
    <>
      <AppRouter />
      <Suspense fallback={null}>
        <InviteCatcher isEnabled={status === 'ready'} />
      </Suspense>
      {/* Оферта для тех, кто зарегистрировался до её появления. Тоже на корне и по той же
          причине, что ловец приглашений: зарегистрированный человек открывает приложение на
          любом маршруте, и привязка к одному экрану значила бы, что часть людей документ
          не увидит вовсе. `isEnabled` — только на пройденной регистрации: внутри онбординга
          акцепт берётся строкой под кнопкой первого шага. */}
      <TermsAcceptSheet isEnabled={status === 'ready'} />
      <DevPanelGate />
    </>
  )
}

/** Корневой компонент приложения. Порядок провайдеров — см. AppProviders. */
export function App() {
  return (
    <ErrorBoundary>
      <AppProviders>
        <AppShell />
      </AppProviders>
    </ErrorBoundary>
  )
}
